import React, { useCallback, useEffect, useState, useRef } from "react";
import { useSession } from "@descope/react-sdk";
import { useLocation } from "react-router-dom";
import "./dashboard.scss";

const Dashboard = () => {
  const [imageData, setImageData] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [noMore, setNoMore] = useState(false);
  const { sessionToken } = useSession();
  const location = useLocation();
  const observer = useRef();
  
  const type = location.pathname.substring(1) || "new"; 

  const fetchData = useCallback(async (page, type) => {
    setLoading(true);
    if (page === 1) {
      setInitialLoading(true);
    }
    try {
      const response = await fetch("/api/data", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${sessionToken}`,
        },
        body: JSON.stringify({ type, page }),
      });
      const data = (await response.json()).body;
      const items = Array.isArray(data) ? data : [];
      setImageData((prevData) => (page === 1 ? items : [...prevData, ...items]));
      if (items.length === 0) {
        setNoMore(true);
      }
      } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [sessionToken]);

  useEffect(() => {
    if (sessionToken) {
      fetchData(page, type);
    }
  }, [sessionToken, page, type, fetchData]);

  useEffect(() => {
    setPage(1);
    setImageData([]);
    setNoMore(false);
  }, [type]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        handleCloseModal();
      }
    };

    if (selectedImage) {
      window.addEventListener("keydown", handleKeyDown);
    } else {
      window.removeEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedImage]);

  const handleImageClick = (url) => {
    setSelectedImage(url);
  };

  const handleCloseModal = () => {
    setSelectedImage(null);
  };

  const lastImageElementRef = useRef();

  useEffect(() => {
    if (loading) return;
    if (noMore) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setPage((prevPage) => prevPage + 1);
      }
    });
    if (lastImageElementRef.current) observer.current.observe(lastImageElementRef.current);
  }, [loading, noMore]);

  if (initialLoading) {
    return (
      <div className="full-screen-loader">
        <div className="loader">Loading...</div>
      </div>
    );
  }

  return (
    <div>
      {initialLoading && (
        <div className="loading-overlay">
          <div className="loader">Loading...</div>
        </div>
      )}
      <div className="image-grid">
        {initialLoading
          ? Array.from({ length: 9 }).map((_, index) => (
              <div key={index} className="image-item placeholder">
                <div className="image-placeholder"></div>
              </div>
            ))
          : imageData.map((item, index) => {
              if (imageData.length === index + 1) {
                return (
                  <div ref={lastImageElementRef} key={index} className="image-item" onClick={() => handleImageClick(item.link)}>
                    <img src={item.link} alt={item.company_name} />
                    <div className="image-overlay">
                      <a href={item.dashboard_link} target="_blank" rel="noopener noreferrer" className="image-link" onClick={e => e.stopPropagation()}>
                        {item.company_name}
                      </a>
                      <a href={item.url} target="_blank" rel="noopener noreferrer" className="image-url" onClick={e => e.stopPropagation()}>
                        {item.url}
                      </a>
                    </div>
                  </div>
                );
              } else {
                return (
                  <div key={index} className="image-item" onClick={() => handleImageClick(item.link)}>
                    <img src={item.link} alt={item.company_name} />
                    <div className="image-overlay">
                      <a href={item.dashboard_link} target="_blank" rel="noopener noreferrer" className="image-link" onClick={e => e.stopPropagation()}>
                        {item.company_name}
                      </a>
                      <a href={item.url} target="_blank" rel="noopener noreferrer" className="image-url" onClick={e => e.stopPropagation()}>
                        {item.url}
                      </a>
                    </div>
                  </div>
                );
              }
            })}
      </div>

      {selectedImage && (
        <div className="modal" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <span className="close" onClick={handleCloseModal}>&times;</span>
            {/* Modal Title Section */}
            <div className="modal-title-section" style={{ marginBottom: "16px", textAlign: "center" }}>
              {(() => {
                const item = imageData.find(img => img.link === selectedImage);
                if (!item) return null;
                return (
                  <>
                    <div style={{ fontWeight: "bold", fontSize: "1.2em" }}>{item.company_name}</div>
                    <div style={{ marginTop: 4 }}>
                      <a
                        href={item.dashboard_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ marginRight: 12 }}
                        onClick={e => e.stopPropagation()}
                      >
                        Dashboard
                      </a>
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                      >
                        Visit Site
                      </a>
                    </div>
                  </>
                );
              })()}
            </div>
            <img src={selectedImage} alt="Selected" className="modal-image" />
          </div>
        </div>
      )}

      <div className="load-more-container">
        {loading && <div className="loading-label">Loading...</div>}
      </div>
    </div>
  );
};

export default Dashboard;